import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet";
import PlaberCard from "../components/plab24/PlaberCard";
import styles from "./Plab24.module.css";
import { supabase } from "../supabase";

async function getImageUrl(bucketName, filePath) {
  const { data, error } = supabase
    .storage
    .from(bucketName)
    .getPublicUrl(filePath);
  if (error) {
    console.error("Error fetching image URL:", error);
    return null;
  }
  console.log("Fetched URL:", data.publicUrl);
  return data.publicUrl;
}

export default function Plab24() {
  const [animationType, setAnimationType] = useState("A");
  const [keyTrigger, setKeyTrigger] = useState(0);
  const [isButtonGroupVisible, setIsButtonGroupVisible] = useState(false);
  const [iconUrls, setIconUrls] = useState([]);
  const [logoUrl, setLogoUrl] = useState("");
  const [backgroundUrls, setBackgroundUrls] = useState({});
  const [activeSection, setActiveSection] = useState(null);
  

  // 각 섹션별 카운트 상태 추가
  const [sectionCounts, setSectionCounts] = useState({
    section2: 0,
    section3: 0,
  });
  const sectionRefs = useRef([]);

  // 섹션별 배경 이미지 정의
  const sectionBackgrounds = {
    section1: "bg_amateur.jpg",
    section2: "bg_semipro.jpg",
    section3: "bg_beginner.jpg",
    section4: "bg_pro.jpg",
    section5: "bg_starter.jpg",
    section6: "bg_rookie.jpg",
    section7: "bg_amateur.jpg",
    section8: "bg_amateur.jpg",
    section9: "bg_amateur.jpg",
  };

  const handleAnimation = (type) => {
    setAnimationType(type);
    setKeyTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    const fetchImages = async () => {
      const logo = await getImageUrl("asset", "plab24_logo.svg");
      
      // 모든 배경 이미지 URL을 가져옴
      const backgrounds = {};
      for (const [key, filename] of Object.entries(sectionBackgrounds)) {
        backgrounds[key] = await getImageUrl("asset", filename);
      }
      
      setLogoUrl(logo);
      setBackgroundUrls(backgrounds);

      const icons = await Promise.all([
        getImageUrl("asset", "ic_plab24_card.svg"),
        getImageUrl("asset", "ic_plab24_played.svg"),
        getImageUrl("asset", "ic_plab24_pom.svg"),
        getImageUrl("asset", "ic_plab24_style.svg"),
        getImageUrl("asset", "ic_plab24_mate.svg"),
        getImageUrl("asset", "ic_plab24_level.svg"),
        getImageUrl("asset", "ic_plab24_poty.svg"),
        getImageUrl("asset", "ic_plab24_plaber100.svg"),
        getImageUrl("asset", "ic_plab24_thanks.svg"),
      ]);

      setIconUrls(icons);
    };

    fetchImages();
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId).scrollIntoView({ behavior: 'smooth' });
  };

  // Section 가시성 감지 및 카운트 애니메이션 트리거
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
            setIsButtonGroupVisible(entry.target.id !== "section1");

            if (entry.target.id === "section2") {
              triggerCountAnimation("section2", 48); // Section2 목표값
            }
            if (entry.target.id === "section3") {
              triggerCountAnimation("section3", 12); // Section3 목표값
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    sectionRefs.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      sectionRefs.current.forEach((section) => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  const triggerCountAnimation = (section, target) => {
    let start = 0;
    const duration = 2000; // 애니메이션 지속 시간 (ms)
    const startTime = performance.now();

    function updateCount(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = progress * (2 - progress); // Ease-out

      setSectionCounts((prevCounts) => ({
        ...prevCounts,
        [section]: Math.floor(target * easedProgress),
      }));

      if (progress < 1) {
        requestAnimationFrame(updateCount);
      }
    }

    requestAnimationFrame(updateCount);
  };
  return (
    <>
    <Helmet>
        <title>Plab24</title>
        <meta property="og:title" content="Plab24" />
        <meta property="og:description" content="나의 2024 플래버 카드를 확인하세요!" />
        <meta property="og:image" content="https://ihcmlcnktfrrojtkrtbt.supabase.co/storage/v1/object/public/asset/OG_plab24.jpg" />
        <meta property="og:url" content="https://plab-design.vercel.app/project/plab24/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Plab24" />
        <meta name="twitter:description" content="나의 2024 플래버 카드를 확인하세요!" />
        <meta name="twitter:image" content="https://ihcmlcnktfrrojtkrtbt.supabase.co/storage/v1/object/public/asset/OG_plab24.jpg" />
      </Helmet>
    <div className={styles.sectionGroup}>
    <div
      id="section1"
      ref={(el) => (sectionRefs.current[0] = el)}
      className={styles.section}
      style={{
        backgroundImage: `url(${backgroundUrls?.section1})`,
      }}
    >
      <div>
        <img src={logoUrl} alt="plab24" />
      </div>
      <PlaberCard animationType={animationType} keyTrigger={keyTrigger} />
      <div className={styles.buttonGroup}>
        <button onClick={() => handleAnimation("A")}>A</button>
        <button onClick={() => handleAnimation("B")}>B</button>
        <button onClick={() => handleAnimation("C")}>C</button>
        <button onClick={() => handleAnimation("D")}>D</button>
      </div>
    </div>
    <div
      id="section2"
      ref={(el) => (sectionRefs.current[1] = el)}
      className={styles.section}
      style={{
        backgroundImage: `url(${backgroundUrls?.section2})`,
      }}
    >
      <div className={styles.sectionHeader}>
        <h2>PLAYED</h2>
      </div>
      <div className={styles.sectionValue}>
        <p>{sectionCounts.section2}</p> {/* Section2 카운트 애니메이션 */}
      </div>
      <div className={styles.sectionDescription}>
        <p>아쿠 님은 올해 48번의 매치에 참여하셨습니다.</p>
      </div>
    </div>
    <div
      id="section3"
      ref={(el) => (sectionRefs.current[2] = el)}
      className={styles.section}
      style={{
        backgroundImage: `url(${backgroundUrls?.section3})`,
      }}
    >
      <div className={styles.sectionHeader}>
        <h2>PLABER OF THE MATCH</h2>
      </div>
      <div className={styles.sectionValue}>
        <p>{sectionCounts.section3}</p> {/* Section3 카운트 애니메이션 */}
      </div>
      <div className={styles.sectionDescription}>
        <p>올해의 매치에서 아쿠 님은 12번 선정되었습니다.</p>
      </div>
    </div>
      <div
        id="section4"
        ref={(el) => (sectionRefs.current[3] = el)}
          className={styles.section}
        style={{
          backgroundImage: `url(${backgroundUrls?.section4})`
        }}
      >
        <div className={styles.sectionHeader}><h2>STYLE</h2></div>
        <div className={styles.sectionValue}><p>PASS</p></div>
        <div className={styles.sectionDescription}><p>Lorem ipsum dolor sit amet consectetur adipisicing elit. Quisquam, quos.</p></div>
      </div>
      <div
        id="section5"
        ref={(el) => (sectionRefs.current[4] = el)}
        className={styles.section}
        style={{
          backgroundImage: `url(${backgroundUrls?.section5})`
        }}
      >
        <div className={styles.sectionHeader}><h2>MATE</h2></div>
        <div className={styles.sectionValue}><p>AQOO</p></div>
        <div className={styles.sectionDescription}><p>Lorem ipsum dolor sit amet consectetur adipisicing elit. Quisquam, quos.</p></div>
      </div>
      <div
        id="section6"
        ref={(el) => (sectionRefs.current[5] = el)}
        className={styles.section}
        style={{
          backgroundImage: `url(${backgroundUrls?.section6})`
        }}
      >
        <div className={styles.sectionHeader}><h2>LEVEL</h2></div>
        <div className={styles.sectionValue}><p>AMA3</p></div>
        <div className={styles.sectionDescription}><p>Lorem ipsum dolor sit amet consectetur adipisicing elit. Quisquam, quos.</p></div>
      </div>
      <div
        id="section7"
        ref={(el) => (sectionRefs.current[6] = el)}
        className={styles.section}
        style={{
          backgroundImage: `url(${backgroundUrls?.section7})`
        }}
      >
        <div className={styles.sectionHeader}><h2>POTY</h2></div>
        <div className={styles.sectionValue}><p>?</p></div>
        <div className={styles.sectionDescription}><p>Lorem ipsum dolor sit amet consectetur adipisicing elit. Quisquam, quos.</p></div>
      </div>
      <div
        id="section8"
        ref={(el) => (sectionRefs.current[7] = el)}
        className={styles.section}
        style={{
          backgroundImage: `url(${backgroundUrls?.section8})`
        }}
      >
        <div className={styles.sectionHeader}><h2>PLABER 100</h2></div>
        <div className={styles.sectionValue}><p>T</p></div>
        <div className={styles.sectionDescription}><p>Lorem ipsum dolor sit amet consectetur adipisicing elit. Quisquam, quos.</p></div>
      </div>
      <div
        id="section9"
        ref={(el) => (sectionRefs.current[8] = el)}
        className={styles.section}
        style={{
          backgroundImage: `url(${backgroundUrls?.section9})`
        }}
      >
        <div className={styles.sectionHeader}><h2>THANKS</h2></div>
        <div className={styles.sectionValue}><p>P</p></div>
        <div className={styles.sectionDescription}><p>Lorem ipsum dolor sit amet consectetur adipisicing elit. Quisquam, quos.</p></div>
      </div>
      <div
        className={`${styles.fixedButtonGroup} ${
          isButtonGroupVisible ? styles.visible : styles.hidden
        }`}
      >
        {iconUrls.map((icon, index) => (
          <div
            key={index}
            onClick={() => scrollToSection(`section${index + 1}`)}
            className={`${styles.button} ${
              activeSection === `section${index + 1}` ? styles.activeButton : ""
            }`}
          >
            <img src={icon} alt={`Icon ${index + 1}`} />
          </div>
        ))}
      </div>
    </div>
    </>
  );
}